"use client";

import { convAILanguages } from "@mw/shared/languagesAndAccents";
import DynamicFlag from "@ui/components/languages/DynamicFlag";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { DottedBorder } from "../../core/GradientBorder";

const LanguageSelectorItem = ({
  language,
  locale,
  layoutId,
  isModalOpen = false,
}: {
  isModalOpen?: boolean;
  language: string;
  layoutId?: string;
  locale: string;
}) => {
  return (
    <m.div
      className={`relative flex items-center gap-10 border-overlay-darker ${isModalOpen ? "p-12 pl-0" : "border-b p-12"}`}
      layoutId={layoutId}
    >
      {isModalOpen && <DottedBorder side='bottom' />}
      <DynamicFlag className='h-20 w-20 rounded-full' locale={locale} size={20} />
      <span className='f-paragraph-03'>{language}</span>
    </m.div>
  );
};

const languagesPreview = convAILanguages.filter(l => ["de", "en", "en-irish", "es", "fr", "hi"].includes(l.value));

export default function LanguagesSelector() {
  const t = useTranslations("ClientKeys.conversational_ai_page");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      // Focus the close button when modal opens
      closeButtonRef.current?.focus();
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Restore body scroll
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Inline preview */}
      <m.div className='bg-white overflow-hidden rounded-lg border border-overlay-darker' layoutId='languages-selector'>
        <div className='flex flex-col'>
          {languagesPreview.map(language => (
            <LanguageSelectorItem
              isModalOpen={false}
              key={language.value}
              language={language.label}
              layoutId={`language-${language.value}`}
              locale={language.value}
            />
          ))}
          <button
            aria-label={t("languages_overview.languages_selector.view_all_languages")}
            className='text-white f-paragraph-03 flex items-center justify-center bg-[#F5F2EF] px-32 py-[14px] transition-colors duration-300 ease-in-out hover:bg-[#EFEBE6]'
            onClick={() => setIsModalOpen(true)}
          >
            {t("languages_overview.languages_selector.supported_languages_count")}
          </button>
        </div>
      </m.div>

      {/* Modal overlay */}
      <AnimatePresence>
        {isModalOpen && (
          <m.div
            animate={{ opacity: 1 }}
            aria-describedby='modal-description'
            aria-labelledby='modal-title'
            aria-modal='true'
            className='fixed inset-0 z-50 flex items-center justify-center'
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={handleClose}
            role='dialog'
          >
            {/* Modal content */}
            <m.div
              className='w-10/12 pb-[5vh] lg:w-full lg:max-w-md'
              layoutId='languages-selector'
              onClick={e => e.stopPropagation()}
              ref={modalRef}
            >
              <div
                className='flex h-[80vh] max-h-[700px] flex-col overflow-hidden rounded-xl bg-overlay-lightest backdrop-blur-md'
                style={{
                  boxShadow:
                    "0px 8px 10px -6px rgba(0, 0, 0, 0.05), 0px 20px 25px -5px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className='flex items-center justify-center px-16 py-36'>
                  <p className='f-heading-04 text-balance text-center'>
                    {t("languages_overview.languages_selector.modal_title")}
                  </p>
                </div>
                <div
                  aria-label='Languages list'
                  className='flex-1 overflow-y-auto px-16'
                  id='modal-description'
                  role='region'
                >
                  {/* Preview languages first */}
                  {languagesPreview.map(language => (
                    <LanguageSelectorItem
                      isModalOpen
                      key={language.value}
                      language={language.label}
                      layoutId={`language-${language.value}`}
                      locale={language.value}
                    />
                  ))}
                  {/* Divider */}
                  <div className='relative p-12 pl-0'>
                    <DottedBorder side='bottom' />
                    <span className='f-paragraph-03 text-light'>
                      {t("languages_overview.languages_selector.all_supported_languages")}
                    </span>
                  </div>
                  {/* Remaining languages */}
                  {convAILanguages
                    .filter(language => !languagesPreview.some(preview => preview.value === language.value))
                    .map(language => (
                      <LanguageSelectorItem
                        isModalOpen
                        key={language.value}
                        language={language.label}
                        locale={language.value}
                      />
                    ))}
                </div>
                <div className='border-default bg-[#D5CAC3]/20 transition-colors duration-300 ease-in-out hover:bg-[#D5CAC3]/30'>
                  <button
                    aria-label='Close languages modal'
                    className='f-paragraph-03 flex h-48 w-full items-center justify-center border-t border-default py-16'
                    onClick={handleClose}
                    ref={closeButtonRef}
                  >
                    {t("languages_overview.languages_selector.close")}
                  </button>
                </div>
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
